import { useQuerystring } from "$routes/_lib/use-querystring";
import { gql } from "@apollo/client";
import {
  AdministrationApproach,
  type PrescriptionSyncPushInput,
  type QueryPullPrescriptionArgs,
  RateUnit,
  TimeUnit,
} from "elephant-web/gql";
import { useMemo } from "react";
import { useMatch } from "react-router-dom";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import { DoseUnit, type Prescription } from "../collections/prescription";
import type { RxDBCollections } from "../provider";
import {
  SyncPrescriptionPullDocument,
  SyncPrescriptionPushDocument,
  type WholeSyncablePrescriptionFragment,
} from "./__generated__/prescription.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicatePrescription = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<Prescription>;
}) => {
  const query = useQuerystring();
  const matchPat = useMatch("/dev-patient/:patientId/*");
  const patientId = matchPat?.params.patientId ?? query.patientUuid;

  const push = useMemo(
    () => ({
      mutation: SyncPrescriptionPushDocument,
      mapRxToGql: mapRxPrescriptionToGqlPrescriptionInput,
    }),
    [],
  );

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncablePrescriptionFragment> | undefined
  >(() => {
    return patientId
      ? {
          query: SyncPrescriptionPullDocument,
          reSyncInterval: { seconds: 30 },
          ...createPersistedCheckpointPullBuilders<
            QueryPullPrescriptionArgs,
            WholeSyncablePrescriptionFragment
          >({
            buildInputVariables: (checkpoint, batchSize) => ({
              input: {
                limit: batchSize,
                filters: { patientId },
                checkpoint,
              },
            }),
            buildPullCheckpoint: (doc) => ({
              id: doc.id,
              updatedAt: doc.meta.updatedAt,
            }),
            entityId: patientId,
            db,
            collectionName: collection?.name,
          }),
        }
      : undefined;
  }, [patientId, db, collection]);

  return useGqlReplication<
    Prescription,
    WholeSyncablePrescriptionFragment,
    PrescriptionSyncPushInput
  >({
    db,
    collection,
    push,
    pull,
    mapGqlToRx: mapGqlSyncablePrescriptionToRxPrescription,
    batchSize: 100,
    expireData: true,
    metadataLocalStorageKeySuffix: `prescription-${patientId}`,
  });
};

const timeUnitMap: Record<string, TimeUnit> = {
  DAY: TimeUnit.Day,
  HOUR: TimeUnit.Hour,
  MONTH: TimeUnit.Month,
  WEEK: TimeUnit.Week,
};

const rateUnitMap: Record<string, RateUnit> = {
  DAY: RateUnit.Day,
  HOUR: RateUnit.Hour,
  ML_HOUR: RateUnit.MlHour,
  MONTH: RateUnit.Month,
  WEEK: RateUnit.Week,
};

const gqlDoseUnitToEnumDoseUnitMap: Record<string, DoseUnit> = {
  mg: DoseUnit.Mg,
  micrograms: DoseUnit.Micrograms,
  g: DoseUnit.G,
  tablet: DoseUnit.Tablet,
  ml: DoseUnit.Ml,
  drop: DoseUnit.Drop,
  puff: DoseUnit.Puff,
  unit: DoseUnit.Unit,
  capsule: DoseUnit.Capsule,
  application: DoseUnit.Application,
  customDose: DoseUnit.CustomDose,
  patch: DoseUnit.Patch,
  sachet: DoseUnit.Sachet,
  suppository: DoseUnit.Suppository,
  pessary: DoseUnit.Pessary,
  device: DoseUnit.Device,
  spray: DoseUnit.Spray,
  dose: DoseUnit.Dose,
};

const mapRxPrescriptionToGqlPrescriptionInput = (
  prescription: WithDeleted<Prescription>,
): PrescriptionSyncPushInput => {
  const frequency =
    prescription.administrationApproach === AdministrationApproach.Outpatient &&
    prescription.frequency
      ? {
          value: Math.floor(prescription.frequency.value),
          unit: prescription.frequency.unit,
        }
      : {
          value: 1,
          unit: TimeUnit.Day,
        };

  return {
    id: prescription.id,
    administrationApproach: prescription.administrationApproach,
    clinicalEvent: {
      id: prescription.clinicalEvent.id,
      type: prescription.clinicalEvent.type,
    },
    prescribableItemType: prescription.prescribableItemType,
    prescribableItemFreeText: prescription.prescribableItemFreeText,
    clinicalProduct: prescription.clinicalProduct
      ? {
          id: prescription.clinicalProduct.id,
          type: prescription.clinicalProduct.type,
        }
      : null,
    dosage: {
      value: prescription.dosage.value,
      unit: prescription.dosage.unit,
    },
    facilityId: prescription.facilityId,
    frequency,
    minimumDoseInterval: prescription.minimumDoseInterval
      ? {
          value: prescription.minimumDoseInterval.value,
          unit: timeUnitMap[prescription.minimumDoseInterval.unit],
        }
      : null,
    note: prescription.note,
    meta: {
      createdAt: prescription.meta.createdAt,
      updatedAt: prescription.meta.updatedAt,
      createdByUserId: prescription.meta.createdByUserId,
      updatedByUserId: prescription.meta.updatedByUserId,
      deleted: prescription._deleted,
    },
    patientId: prescription.patientId,
    prescribedByUserId: prescription.prescribedByUserId,
    rate: prescription.rate
      ? {
          value: Math.floor(prescription.rate.value),
          unit: rateUnitMap[prescription.rate.unit],
        }
      : null,
    startsAt: prescription.startsAt,
    stopsAt: prescription.stopsAt,
  };
};

export const mapGqlSyncablePrescriptionToRxPrescription = (
  prescription: WholeSyncablePrescriptionFragment,
): WithDeleted<Prescription> => {
  return {
    id: prescription.id,
    administrationApproach: prescription.administrationApproach,
    cancellationDetails: prescription.cancellationDetails
      ? {
          at: prescription.cancellationDetails.at,
          by: prescription.cancellationDetails.by?._id || null,
          reason: prescription.cancellationDetails.reason,
        }
      : null,
    clinicalEvent: {
      id: prescription.clinicalEvent.id,
      type: prescription.clinicalEvent.type,
    },
    prescribableItemType: prescription.prescribableItemType,
    prescribableItemFreeText: prescription.prescribableItemFreeText ?? null,
    clinicalProduct: prescription.clinicalProduct
      ? {
          id: prescription.clinicalProduct.id,
          type: prescription.clinicalProduct.type,
        }
      : null,
    dosage: {
      value: prescription.dosage.value,
      unit:
        gqlDoseUnitToEnumDoseUnitMap[prescription.dosage.unit.toLowerCase()] ??
        DoseUnit.Unit,
    },
    facilityId: prescription.facilityId,
    frequency: prescription.frequency
      ? {
          value: prescription.frequency.value,
          unit: prescription.frequency.unit,
        }
      : null,
    meta: {
      createdAt: prescription.meta.createdAt,
      updatedAt: prescription.meta.updatedAt,
      createdByUserId: prescription.meta.createdByUserId,
      updatedByUserId: prescription.meta.updatedByUserId,
    },
    minimumDoseInterval: prescription.minimumDoseInterval
      ? {
          value: prescription.minimumDoseInterval.value,
          unit: prescription.minimumDoseInterval.unit,
        }
      : null,
    note: prescription.note || null,
    patientId: prescription.patientId,
    prescribedByUserId: prescription.prescribedByUserId,
    rate: prescription.rate
      ? {
          value: prescription.rate.value,
          unit: prescription.rate.unit,
        }
      : null,
    startsAt: prescription.startsAt,
    stopsAt: prescription.stopsAt,
    _deleted: prescription.meta.deleted,
  };
};

gql`
  fragment WholeSyncablePrescription on SyncablePrescription {
    id
    administrationApproach
    cancellationDetails {
      at
      by {
        _id
      }
      reason
    }
    clinicalEvent {
      id
      type
    }
    prescribableItemType
    clinicalProduct {
      id
      type
    }
    prescribableItemFreeText
    dosage {
      value
      unit
    }
    facilityId
    frequency {
      value
      unit
    }
    meta {
      createdAt
      createdByUserId
      deleted
      updatedAt
      updatedByUserId
    }
    minimumDoseInterval {
      value
      unit
    }
    note
    patientId
    prescribedByUserId
    rate {
      value
      unit
    }
    startsAt
    stopsAt
  }
`;

gql`
  mutation SyncPrescriptionPush($input: PushPrescriptionInput!) {
    pushPrescription(input: $input) {
      rows {
        ...WholeSyncablePrescription
      }
    }
  }
`;

gql`
  query SyncPrescriptionPull($input: PullPrescriptionInput!) {
    pullPrescription(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncablePrescription
      }
    }
  }
`;
